<template>
  <nav class="navbar">
    <div class="navbar-brand">
      <router-link to="/dashboard" class="logo">
        <span class="logo-text">FuckYouInc</span>
      </router-link>
      <button class="mobile-menu-toggle" :class="{ 'active': isMobileMenuOpen }" @click="toggleMobileMenu">
        <span class="menu-icon"></span>
      </button>
    </div>
    
    <div class="navbar-menu" :class="{ 'is-active': isMobileMenuOpen }">
      <div class="navbar-links">
        <router-link to="/dashboard" class="nav-link" :class="{ 'active': activePath === '/dashboard' }">
          <span>Dashboard</span>
        </router-link>
        <router-link to="/markets" class="nav-link" :class="{ 'active': activePath === '/markets' }">
          <span>Markets</span>
        </router-link>
        <router-link to="/trade" class="nav-link" :class="{ 'active': activePath === '/trade' }">
          <span>Trade</span>
        </router-link>
        <router-link to="/wallet" class="nav-link" :class="{ 'active': activePath === '/wallet' }">
          <span>Wallet</span>
        </router-link>
      </div>
      
      <div class="navbar-user">
        <div class="notifications">
          <button class="notification-bell" @click="toggleNotifications">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            <span v-if="unreadNotifications > 0" class="notification-badge">{{ unreadNotifications }}</span>
          </button>
          <div v-if="showNotifications" class="notification-dropdown">
            <div class="notification-header">
              <h3>Notifications</h3>
              <button class="mark-all-read" @click="markAllAsRead">Mark all as read</button>
            </div>
            <div class="notification-list" v-if="notifications.length > 0">
              <div 
                v-for="notification in notifications" 
                :key="notification.id" 
                class="notification-item"
                :class="{ 'unread': !notification.read }"
              >
                <div class="notification-content">
                  <p>{{ notification.message }}</p>
                  <span class="notification-time">{{ notification.time }}</span>
                </div>
              </div>
            </div>
            <div v-else class="empty-notifications">
              No notifications
            </div>
          </div>
        </div>
        
        <div class="user-menu">
          <button class="user-button" @click="toggleUserMenu">
            <div class="avatar">
              <span>{{ userInitials }}</span>
            </div>
          </button>
          <div v-if="showUserMenu" class="user-dropdown">
            <div class="user-info">
              <div class="avatar large">
                <span>{{ userInitials }}</span>
              </div>
              <div class="user-details">
                <h3>{{ userName }}</h3>
                <p>{{ userEmail }}</p>
              </div>
            </div>
            <div class="dropdown-divider"></div>
            <ul class="dropdown-menu">
              <li>
                <router-link to="/settings" class="dropdown-item">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  <span>Settings</span>
                </router-link>
              </li>
              <li>
                <button class="dropdown-item" @click="logout">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  <span>Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();

// User data (this would come from a store in a real app)
const userName = ref('John Doe');
const userEmail = ref('john.doe@example.com');
const userInitials = computed(() => {
  if (!userName.value) return '';
  return userName.value
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase();
});

// Navigation state
const isMobileMenuOpen = ref(false);
const showUserMenu = ref(false);
const showNotifications = ref(false);
const activePath = computed(() => route.path);

// Sample notifications (would come from an API/store in a real app)
const notifications = ref([
  { id: 1, message: 'Your deposit of 1.5 BTC has been confirmed', time: '2 hours ago', read: false },
  { id: 2, message: 'Market alert: ETH up 5% in the last hour', time: '5 hours ago', read: false },
  { id: 3, message: 'Your limit order was filled', time: '1 day ago', read: true }
]);

const unreadNotifications = computed(() => {
  return notifications.value.filter(notification => !notification.read).length;
});

// Toggle functions
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  
  // Close other menus if opening mobile menu
  if (isMobileMenuOpen.value) {
    showUserMenu.value = false;
    showNotifications.value = false;
  }
};

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value;
  
  // Close notifications if opening user menu
  if (showUserMenu.value) {
    showNotifications.value = false;
  }
};

const toggleNotifications = () => {
  showNotifications.value = !showNotifications.value;
  
  // Close user menu if opening notifications
  if (showNotifications.value) {
    showUserMenu.value = false;
  }
};

const markAllAsRead = () => {
  notifications.value.forEach(notification => {
    notification.read = true;
  });
};

const logout = () => {
  // Perform logout logic here
  localStorage.removeItem('auth_token');
  router.push('/login');
};

// Close dropdowns when clicking outside
const handleClickOutside = (event) => {
  const userMenuElement = document.querySelector('.user-menu');
  const notificationsElement = document.querySelector('.notifications');
  
  if (userMenuElement && !userMenuElement.contains(event.target)) {
    showUserMenu.value = false;
  }
  
  if (notificationsElement && !notificationsElement.contains(event.target)) {
    showNotifications.value = false;
  }
};

// Close mobile menu on route change
const closeMenuOnRouteChange = () => {
  isMobileMenuOpen.value = false;
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  router.afterEach(closeMenuOnRouteChange);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #0a0e17;
  border-bottom: 1px solid #1a2334;
  padding: 0 20px;
  height: 64px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.navbar-brand {
  display: flex;
  align-items: center;
}

.logo {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  text-decoration: none;
  display: flex;
  align-items: center;
}

.logo-text {
  background: linear-gradient(45deg, #1347e4, #13b9e4);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: bold;
}

.mobile-menu-toggle {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  position: relative;
  z-index: 1001;
}

.menu-icon {
  display: block;
  width: 24px;
  height: 2px;
  background-color: #fff;
  position: relative;
  transition: background-color 0.2s;
}

.menu-icon::before,
.menu-icon::after {
  content: '';
  position: absolute;
  width: 24px;
  height: 2px;
  background-color: #fff;
  transition: transform 0.2s;
}

.menu-icon::before {
  top: -8px;
}

.menu-icon::after {
  bottom: -8px;
}

.mobile-menu-toggle.active .menu-icon {
  background-color: transparent;
}

.mobile-menu-toggle.active .menu-icon::before {
  transform: rotate(45deg);
  top: 0;
}

.mobile-menu-toggle.active .menu-icon::after {
  transform: rotate(-45deg);
  bottom: 0;
}

.navbar-menu {
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: space-between;
  margin-left: 40px;
}

.navbar-links {
  display: flex;
}

.nav-link {
  display: flex;
  align-items: center;
  color: #788195;
  text-decoration: none;
  padding: 0 16px;
  height: 64px;
  font-size: 15px;
  transition: color 0.2s ease, border-bottom 0.2s ease;
  position: relative;
}

.nav-link:hover {
  color: #fff;
  text-decoration: none;
}

.nav-link.active {
  color: #fff;
  font-weight: 500;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 16px;
  right: 16px;
  height: 3px;
  background-color: #1347e4;
  border-radius: 3px 3px 0 0;
}

.navbar-user {
  display: flex;
  align-items: center;
}

.notifications {
  position: relative;
  margin-right: 16px;
}

.notification-bell {
  background: none;
  border: none;
  color: #788195;
  cursor: pointer;
  padding: 8px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.notification-bell:hover {
  color: #fff;
}

.notification-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  background-color: #171d28;
  border: 1px solid #252f3d;
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  z-index: 10;
  margin-top: 8px;
  overflow: hidden;
  animation: dropdown-fade 0.2s ease;
}

.notification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #252f3d;
}

.notification-header h3 {
  font-size: 16px;
  color: #fff;
  margin: 0;
}

.mark-all-read {
  background: none;
  border: none;
  color: #1347e4;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
}

.notification-list {
  max-height: 320px;
  overflow-y: auto;
}

.notification-item {
  padding: 16px;
  border-bottom: 1px solid #252f3d;
  transition: background-color 0.2s;
}

.notification-item:hover {
  background-color: #1a2334;
}

.notification-item.unread {
  background-color: rgba(19, 71, 228, 0.05);
}

.notification-content p {
  margin: 0 0 8px;
  color: #fff;
  font-size: 14px;
}

.notification-time {
  color: #788195;
  font-size: 12px;
}

.empty-notifications {
  padding: 24px 16px;
  text-align: center;
  color: #788195;
}

.user-menu {
  position: relative;
}

.user-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #1347e4;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.avatar.large {
  width: 48px;
  height: 48px;
  font-size: 18px;
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 280px;
  background-color: #171d28;
  border: 1px solid #252f3d;
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  z-index: 10;
  margin-top: 8px;
  overflow: hidden;
  animation: dropdown-fade 0.2s ease;
}

.user-info {
  display: flex;
  align-items: center;
  padding: 16px;
}

.user-details {
  margin-left: 12px;
}

.user-details h3 {
  margin: 0 0 4px;
  color: #fff;
  font-size: 16px;
}

.user-details p {
  margin: 0;
  color: #788195;
  font-size: 14px;
}

.dropdown-divider {
  height: 1px;
  background-color: #252f3d;
  margin: 0;
}

.dropdown-menu {
  list-style: none;
  padding: 8px 0;
  margin: 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: #1a2334;
  text-decoration: none;
}

.dropdown-item svg {
  margin-right: 8px;
  color: #788195;
}

@keyframes dropdown-fade {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 992px) {
  .mobile-menu-toggle {
    display: block;
  }
  
  .navbar-menu {
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #0a0e17;
    flex-direction: column;
    align-items: stretch;
    padding: 24px;
    margin-left: 0;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    z-index: 999;
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
  }
  
  .navbar-menu.is-active {
    transform: translateX(0);
    left: 0;
  }
  
  .navbar-links {
    flex-direction: column;
    width: 100%;
    margin-bottom: 24px;
  }
  
  .nav-link {
    padding: 16px 0;
    border-bottom: 1px solid #252f3d;
    width: 100%;
    font-size: 18px;
  }
  
  .navbar-user {
    flex-direction: column-reverse;
    width: 100%;
  }
  
  .user-menu {
    width: 100%;
    margin-bottom: 24px;
  }
  
  .notifications {
    width: 100%;
  }
  
  .notification-bell {
    width: 100%;
    padding: 16px 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    border-bottom: 1px solid #252f3d;
  }
  
  .notification-bell svg {
    margin-right: 12px;
  }
  
  .notification-bell::after {
    content: 'Notifications';
    font-size: 18px;
  }
  
  .notification-dropdown,
  .user-dropdown {
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    border-radius: 0;
    box-shadow: none;
  }
}

@media (max-width: 480px) {
  .navbar {
    padding: 0 12px;
  }
  
  .logo-text {
    font-size: 20px;
  }
  
  .notification-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px;
  }
  
  .notification-header h3 {
    margin-bottom: 8px;
  }
  
  .notification-item {
    padding: 12px;
  }
  
  .user-info {
    padding: 12px;
  }
  
  .dropdown-item {
    padding: 12px;
  }
}
</style> 